import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Building2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { differenceInDays, parseISO, format } from "date-fns";

const NEGOTIATION_TIPS = [
  "Request a market rent comparison from your broker — Miami retail rents shifted significantly in 2024.",
  "Negotiate tenant improvement (TI) allowance — typical: $15–$40/sqft for restaurant spaces.",
  "Ask for 2–3 months of free rent in exchange for a longer lease term.",
  "Include an assignment clause so you can transfer the lease if you sell the business.",
  "Cap annual rent escalations at 3% instead of CPI-based increases.",
  "Request right of first refusal on adjacent spaces for future expansion.",
  "Ensure your lease allows signage and outdoor seating if applicable.",
  "Negotiate an exclusive use clause to prevent a competing restaurant in the same plaza.",
];

export default function LeaseTracker() {
  const [leaseEnd, setLeaseEnd] = useState("");
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    if (leaseEnd) {
      localStorage.setItem("oplif_lease_end", leaseEnd);
      setSaved(true);
    }
  };

  useState(() => {
    const stored = localStorage.getItem("oplif_lease_end");
    if (stored) { setLeaseEnd(stored); setSaved(true); }
  });

  const days = saved && leaseEnd ? differenceInDays(parseISO(leaseEnd), new Date()) : null;
  const showGuide = days !== null && days <= 180;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-primary" /> Lease Tracker</CardTitle>
      </CardHeader>
      <CardContent>
        {!saved ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Enter your lease expiration date and we'll track the countdown, plus surface negotiation tips when it's time to renew.</p>
            <div><Label>Lease Expiration Date</Label><Input type="date" value={leaseEnd} onChange={e => setLeaseEnd(e.target.value)} /></div>
            <Button size="sm" onClick={handleSave} disabled={!leaseEnd}>Save</Button>
          </div>
        ) : days !== null ? (
          <div className="space-y-4">
            <div className={`p-4 rounded-lg border ${days < 90 ? "bg-destructive/10 border-destructive/30" : days <= 180 ? "bg-accent/10 border-accent/30" : "bg-primary/10 border-primary/30"}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-muted-foreground">Lease expires</div>
                  <div className="text-lg font-medium text-foreground">{format(parseISO(leaseEnd), "MMMM d, yyyy")}</div>
                </div>
                <div className={`text-3xl font-medium ${days < 90 ? "text-destructive" : days <= 180 ? "text-accent" : "text-primary"}`}>
                  {days < 0 ? `${Math.abs(days)}d overdue` : `${days}d`}
                </div>
              </div>
            </div>

            {showGuide ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-accent" />
                  <h4 className="text-sm font-medium text-foreground">Lease Negotiation Guide — Start Now</h4>
                </div>
                <div className="space-y-2">
                  {NEGOTIATION_TIPS.map((tip, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <span className="text-primary font-medium shrink-0">{i + 1}.</span>
                      <span className="text-muted-foreground">{tip}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-primary">
                <CheckCircle2 className="w-4 h-4" />
                <span>You're in good shape. Renewal tips will appear 180 days before your lease expires.</span>
              </div>
            )}

            <Button variant="outline" size="sm" onClick={() => { setSaved(false); localStorage.removeItem("oplif_lease_end"); }}>
              Change Date
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
